import { getActiveOrgId } from '@/lib/activeOrg';
import { filterByIdOrShortId } from '@/lib/shortId';
import { supabase } from '@/lib/supabase';
import { paginationRange, buildPaginatedResponse, throwIfError } from '@/lib/supabase-helpers';
import { SeoProject } from '@/types/api/seo';

function mapRow(row: any): SeoProject {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    organizationId: row.organization_id,
    domain: row.domain,
    name: row.name,
    siteUrl: row.site_url ?? null,
    ga4PropertyId: row.ga4_property_id ?? null,
    ga4PropertyName: row.ga4_property_name ?? null,
    wpSiteUrl: row.wp_site_url ?? null,
    wpUsername: row.wp_username ?? null,
    wpSeoPlugin: row.wp_seo_plugin ?? null,
    wpConnectedAt: row.wp_connected_at ?? null,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: row.deleted_at,
  };
}

const seoProjectsService = {
  async getAll({
    page,
    take,
  }: {
    page: number;
    take: number;
  }): Promise<{
    data: SeoProject[];
    count: number;
    currentPage: number;
    lastPage: number;
    nextPage: number | null;
    prevPage: number | null;
  }> {
    const [from, to] = paginationRange(page, take);
    const orgId = getActiveOrgId();
    let query = supabase
      .from('seo_projects')
      .select('*', { count: 'exact' })
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data, error, count } = await query;

    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapRow), count ?? 0, page, take);
  },

  async getById(id: string): Promise<SeoProject> {
    let query = supabase.from('seo_projects').select('*');
    query = filterByIdOrShortId(query, id);
    const { data, error } = await query.single();

    if (error) throw error;
    return mapRow(data);
  },

  async create(payload: {
    organizationId: string;
    domain: string;
    name: string;
    siteUrl?: string;
    ga4PropertyId?: string;
    ga4PropertyName?: string;
    createdBy: string;
  }): Promise<SeoProject> {
    const insert: Record<string, unknown> = {
      organization_id: payload.organizationId,
      domain: payload.domain,
      name: payload.name,
      created_by: payload.createdBy,
    };
    if (payload.siteUrl !== undefined) insert.site_url = payload.siteUrl;
    if (payload.ga4PropertyId !== undefined) insert.ga4_property_id = payload.ga4PropertyId;
    if (payload.ga4PropertyName !== undefined) insert.ga4_property_name = payload.ga4PropertyName;

    const result = await supabase.from('seo_projects').insert(insert).select().single();

    return mapRow(throwIfError(result));
  },

  async update(
    id: string,
    payload: {
      domain?: string;
      name?: string;
      siteUrl?: string;
      ga4PropertyId?: string;
      ga4PropertyName?: string;
    }
  ): Promise<SeoProject> {
    const updates: Record<string, unknown> = {};
    if (payload.domain !== undefined) updates.domain = payload.domain;
    if (payload.name !== undefined) updates.name = payload.name;
    if (payload.siteUrl !== undefined) updates.site_url = payload.siteUrl;
    if (payload.ga4PropertyId !== undefined) updates.ga4_property_id = payload.ga4PropertyId;
    if (payload.ga4PropertyName !== undefined) updates.ga4_property_name = payload.ga4PropertyName;

    const result = await supabase
      .from('seo_projects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    return mapRow(throwIfError(result));
  },

  async delete(id: string): Promise<SeoProject> {
    const result = await supabase
      .from('seo_projects')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    return mapRow(throwIfError(result));
  },
};

export default seoProjectsService;
